// src/modules/Projects/ProjectDetails.js
import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";

import { secureGet, secureDelete, securePut, securePost } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";

import { PrimaryButton } from "../../components/PrimaryButton";
import Breadcrumbs from "../../components/Breadcrumbs";
import SnackbarNotification from "../../components/SnackbarNotification";
import Rows from "../../components/Rows";
import ModuleWrapper from "../../components/ModuleWrapper";
import Filter from "../../components/Filter";
import DetailsHeader from "../../components/DetailsHeader";
import OptionsMenu from "../../components/OptionsMenu";
import ProductRow from "../../components/ProductRow";
import LoadingLogo from "../../components/LoadingLogo";
import GenericCard from "../Users/GenericCard";
import UserContext from "../../Shared/UserContext";
import DeletePopup from '../../components/DeletePopup';
import TaskForm from './TaskForm';

import AddIcon from "../../icons/plus.svg";
import plusIcon from "../../icons/plus.svg";
import InfoIcon from "../../icons/info.svg";
import CheckIcon from "../../icons/checkG.svg";
import XCircleIcon from "../../icons/xRed.svg";
import UserIcon from "../../icons/user-plus.svg";
import EditIcon from "../../icons/edit.svg";
import EyeIcon from "../../icons/eye.svg";
import DownloadIcon from "../../icons/downloadB.svg";
import DeleteIcon from "../../icons/trashR.svg";
import GenerateIcon from "../../icons/printer.svg";
import checkIcon from "../../icons/checkB.svg";

const STATUS_CHIP = (s) => (
  <span
    className="text-white text-[9px] font-semibold px-1 py-0.5 rounded-full inline-block w-fit text-center"
    style={{ backgroundColor: chipColor(s) }}
  >
    {s}
  </span>
);

const chipColor = (s) => {
  const MAP = {
    "À faire": "#A8ADC3",
    "En pause": "#2D7CF8",
    "En cours": "#FEA26F",
    "Bloqué": "#EF456E",
    "Terminé": "#0AD69F",
    "VALIDATED": "#0AD69F",
    "REJECTED": "#EF466F",
    "PENDING": "#A8ADC3",
  };
  return MAP[s] || "#000000";
};

const ProjectsDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { id } = useParams();
  const { state: { role } } = useContext(UserContext);

  const [activeTab, setActiveTab] = useState("tasks");
  const [loading, setLoading] = useState(false);
  const [showModule, setShowModule] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [showDocumentModule, setShowDocumentModule] = useState(false);
  const [, setSelectedDocument] = useState(null);
  const [deleteDocumentPopupOpen, setDeleteDocumentPopupOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [generatingPrefacture, setGeneratingPrefacture] = useState(false);
  const [finishingProject, setFinishingProject] = useState(false);
  const [techs, setTechs] = useState([]);
  const [filters, setFilters] = useState({
    needle: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const [project, setProject] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [fr, setFr] = useState(null);
  const [allTasks, setAllTasks] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [documentFilters, setDocumentFilters] = useState({ type: "" });
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);

  const [loadedTabs, setLoadedTabs] = useState({
    tasks: false,
    quotes: false,
    orders: false,
    deliveryNotes: false,
    documents: false,
    history: false,
  });
  
  const [tabLoading, setTabLoading] = useState({
    tasks: false,
    quotes: false,
    orders: false,
    deliveryNotes: false,
    documents: false,
    history: false,
  });

  const tabFetchInFlight = useRef({
    quotes: false,
    orders: false,
    deliveryNotes: false,
    documents: false,
    history: false,
  });

  // Fetch project details
  const fetchProject = useCallback(async () => {
    const abortController = new AbortController();
    setLoading(true);
    try {
      const response = await secureGet(`/projects/${id}`, showSnackbar, {
        signal: abortController.signal
      });
      console.log("project fetched data:", response.data);
      
      const projectData = response.data;
      setProject(projectData);
      
      // Set tasks from project.tasks (lazy loading will handle display)
      if (projectData?.tasks) {
        setAllTasks(projectData.tasks || []);
        setLoadedTabs(prev => ({ ...prev, tasks: true }));
      }
      
      // Fetch vehicle if vehicleId exists
      if (projectData?.vehicle?.id) {
        await fetchVehicle(projectData.vehicle.id);
      }
      
      // Fetch reception document if receptionDocumentId exists
      if (projectData?.receptionDocumentId) {
        await fetchReceptionDocument(projectData.receptionDocumentId);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch project:", error);
      }
    } finally {
      setLoading(false);
    }
    
    return () => {
      abortController.abort();
    };
  }, [id]);

  // Fetch vehicle data
  const fetchVehicle = async (vehicleId) => {
    if (!vehicleId) return;

    const abortController = new AbortController();
    try {
      const response = await secureGet(`/vehicles/${vehicleId}`, showSnackbar, {
        signal: abortController.signal
      });
      setVehicle(response.data);
      console.log("vehicle fetched data:", response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch vehicle:", error);
      }
    }
  };

  // Fetch reception document (fr)
  const fetchReceptionDocument = async (receptionDocumentId) => {
    console.log("receptionDocumentId", receptionDocumentId);
    if (!receptionDocumentId) return;

    const abortController = new AbortController();
    try {
      const response = await secureGet(`/reception-documents/${receptionDocumentId}`, showSnackbar, {
        signal: abortController.signal
      });
      setFr(response.data);
      console.log("reception document fetched data:", response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch reception document:", error);
      }
    }
  };

  // Fetch tasks - use project.tasks on initial load, fetch from API after modifications
  const fetchTasks = useCallback(async (forceFetch = false) => {
    if (!project) return;

    // On initial load, use project.tasks if available and not forcing fetch
    if (!forceFetch && loadedTabs.tasks && project.tasks) {
      return;
    }

    setTabLoading(prev => ({ ...prev, tasks: true }));
    try {
      if (forceFetch || !project.tasks) {
        // Fetch from API when tasks have been modified or project.tasks is not available
        const response = await secureGet(`/projects/${project.id}/tasks`, showSnackbar);
        if (response?.data) {
          setAllTasks(response.data || []);
          setLoadedTabs(prev => ({ ...prev, tasks: true }));
        }
      } else {
        // Initial load - use project.tasks
        setAllTasks(project.tasks || []);
        setLoadedTabs(prev => ({ ...prev, tasks: true }));
      }
    } catch (error) {
      console.error("Failed to load tasks:", error);
    } finally {
      setTabLoading(prev => ({ ...prev, tasks: false }));
    }
  }, [project, loadedTabs.tasks, showSnackbar]);

  // Fetch quotes (lazy loading)
  const fetchQuotes = useCallback(async () => {
    if (!project?.receptionDocumentId || loadedTabs.quotes || tabFetchInFlight.current.quotes) return;
    
    const abortController = new AbortController();
    tabFetchInFlight.current.quotes = true;
    setTabLoading(prev => ({ ...prev, quotes: true }));
    try {
      const response = await secureGet(`/quotes/reception-document/${project.receptionDocumentId}`, showSnackbar, {
        signal: abortController.signal
      });
      console.log("quotes fetched data:", response.data);
      
      // Only store the latest version (the quote returned from API)
      const quoteData = response.data || null;
      setQuotes(quoteData ? [quoteData] : []);
      setLoadedTabs(prev => ({ ...prev, quotes: true }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch quotes:", error);
        // Handle 404 silently (no quote found)
        if (error.response?.status === 404) {
          setQuotes([]);
          setLoadedTabs(prev => ({ ...prev, quotes: true }));
        }
      }
    } finally {
      tabFetchInFlight.current.quotes = false;
      setTabLoading(prev => ({ ...prev, quotes: false }));
    }
    
    return () => {
      abortController.abort();
    };
  }, [project?.receptionDocumentId, loadedTabs.quotes]);

  // Fetch orders (lazy loading)
  const fetchOrders = useCallback(async () => {
    if (!id || !project?.receptionDocumentId || loadedTabs.orders || tabFetchInFlight.current.orders) return;
    
    const abortController = new AbortController();
    tabFetchInFlight.current.orders = true;
    setTabLoading(prev => ({ ...prev, orders: true }));
    try {
      const response = await secureGet(`/carts/reception-document/${project.receptionDocumentId}`, showSnackbar, {
        signal: abortController.signal
      });
      console.log("orders fetched data:", response.data);
      
      setOrders(response.data || []);
      setLoadedTabs(prev => ({ ...prev, orders: true }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch orders:", error);
      }
    } finally {
      tabFetchInFlight.current.orders = false;
      setTabLoading(prev => ({ ...prev, orders: false }));
    }
    
    return () => {
      abortController.abort();
    };
  }, [id, project?.receptionDocumentId, loadedTabs.orders]);

  // Fetch delivery notes (lazy loading)
  const fetchDeliveryNotes = useCallback(async () => {
    if (!id || !project?.id || loadedTabs.deliveryNotes || tabFetchInFlight.current.deliveryNotes) return;

    const abortController = new AbortController();
    tabFetchInFlight.current.deliveryNotes = true;
    setTabLoading(prev => ({ ...prev, deliveryNotes: true }));
    try {
      const response = await secureGet(`/deliveryNotes/project/${project.id}`, showSnackbar, {
        signal: abortController.signal
      });
      console.log("delivery notes fetched data:", response.data);
      setDeliveryNotes(response.data || []);
      setLoadedTabs(prev => ({ ...prev, deliveryNotes: true }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch delivery notes:", error);
      }
    } finally {
      tabFetchInFlight.current.deliveryNotes = false;
      setTabLoading(prev => ({ ...prev, deliveryNotes: false }));
    }
  }, [id, project?.id, loadedTabs.deliveryNotes]);

  // Fetch documents (lazy loading)
  const fetchDocuments = useCallback(async () => {
    if (!project?.receptionDocumentId || loadedTabs.documents || tabFetchInFlight.current.documents) return;
    
    const abortController = new AbortController();
    tabFetchInFlight.current.documents = true;
    setTabLoading(prev => ({ ...prev, documents: true }));
    try {
      const params = new URLSearchParams();
      if (documentFilters.type) params.append("type", documentFilters.type);
      const url = `/reception-documents/${project.receptionDocumentId}/attachments${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await secureGet(url, showSnackbar, {
        signal: abortController.signal
      });
      console.log("documents fetched data:", response.data);
      
      setDocuments(response.data || []);
      setLoadedTabs(prev => ({ ...prev, documents: true }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch documents:", error);
      }
    } finally {
      tabFetchInFlight.current.documents = false;
      setTabLoading(prev => ({ ...prev, documents: false }));
    }
    
    return () => {
      abortController.abort();
    };
  }, [project?.receptionDocumentId, loadedTabs.documents, documentFilters.type]);

  const fetchDocumentTypeOptions = useCallback(async () => {
    try {
      const res = await secureGet('/attachment-document-types', showSnackbar);
      const list = res?.data || [];
      setDocumentTypeOptions(list.map((item) => ({ value: item.id, label: item.name })));
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error fetching attachment document types:', err);
    }
  }, []);

  useEffect(() => {
    fetchDocumentTypeOptions();
  }, [fetchDocumentTypeOptions]);

  // Fetch history (lazy loading)
  const fetchHistory = useCallback(async () => {
    if (!project || loadedTabs.history || tabFetchInFlight.current.history) return;

    const abortController = new AbortController();
    tabFetchInFlight.current.history = true;
    setTabLoading(prev => ({ ...prev, history: true }));

    try {
      const allHistoryEntries = [];

      allHistoryEntries.push({
        id: `project-creation-${project.id}`,
        date: project.createdAt,
        user: project.createdByUsername,
        description: 'Projet créé',
        type: 'CREATED',
        dotColor: '#2D7CF8',
      });

      // Ensure tasks are loaded
      let tasksToUse = allTasks;
      if (!tasksToUse || tasksToUse.length === 0) {
        // Fetch tasks if not already loaded
        try {
          const tasksResponse = await secureGet(`/projects/${project.id}/tasks`, showSnackbar, {
            signal: abortController.signal
          });
          tasksToUse = tasksResponse?.data || [];
        } catch (error) {
          if (error.name !== 'AbortError' && error.response?.status !== 404) {
            console.error("Failed to fetch tasks for history:", error);
          }
          tasksToUse = [];
        }
      }

      // Fetch history for each task
      if (tasksToUse && tasksToUse.length > 0) {
        const taskHistoryPromises = tasksToUse.map(async (task) => {
          try {
            const response = await secureGet(`/projects/tasks/${task.id}/history`, showSnackbar, {
              signal: abortController.signal
            });
            console.log("task history fetched data:", response.data);
            return { task, histories: response.data || [] };
          } catch (error) {
            if (error.name !== 'AbortError' && error.response?.status !== 404) {
              console.error(`Failed to fetch history for task ${task.id}:`, error);
            }
            return { task, histories: [] };
          }
        });

        const taskHistoriesResults = await Promise.all(taskHistoryPromises);

        // Flatten and format task histories
        taskHistoriesResults.forEach(({ task, histories }) => {
          histories.forEach((historyItem) => {
            // Skip entries where changedByUsername is null
            if (!historyItem.changedByUsername) {
              return;
            }

            // Determine dot color based on newStatus
            let dotColor = '#2D7CF8'; // Default blue
            if (historyItem.newStatus === 'BLOCKED' || historyItem.blockedReason) {
              dotColor = '#EF466F'; // Red
            } else if (historyItem.newStatus === 'DONE' || historyItem.newStatus === 'TERMINÉ') {
              dotColor = '#06D6A0'; // Green
            }

            // Format description: "taskNumber a été marqué comme newStatus by changedByUsername" (date shown separately on first line)
            const statusLabel = historyItem.newStatus
              ? t(`task_status.${historyItem.newStatus}`, historyItem.newStatus)
              : '—';
            const description = `${task.taskNumber} a été marqué comme ${statusLabel} par ${historyItem.changedByUsername}`;

            allHistoryEntries.push({
              id: `task-${task.id}-history-${historyItem.id || Date.now()}`,
              date: historyItem.changedAt,
              user: historyItem.changedByUsername,
              description: description,
              type: historyItem.newStatus,
              dotColor,
              taskNumber: task.taskNumber,
            });
          });
        });
      }

      // Add project finishedAt if not null
      if (project.finishedAt) {
        allHistoryEntries.push({
          id: `project-finished-${project.id}`,
          date: project.finishedAt,
          user: project.finishedByUsername || project.updatedByUsername || '—',
          description: `Projet terminé`,
          type: 'FINISHED',
          dotColor: '#06D6A0', // Green
        });
      }

      // Add project deliveredAt if not null
      if (project.deliveredAt) {
        allHistoryEntries.push({
          id: `project-delivered-${project.id}`,
          date: project.deliveredAt,
          user: project.deliveredByUsername || project.updatedByUsername || '—',
          description: `Projet livré`,
          type: 'DELIVERED',
          dotColor: '#06D6A0', // Green
        });
      }

      // Sort by date (newest first)
      allHistoryEntries.sort((a, b) => new Date(b.date) - new Date(a.date));

      setHistoryEntries(allHistoryEntries);
      setLoadedTabs(prev => ({ ...prev, history: true }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch history:", error);
      }
    } finally {
      tabFetchInFlight.current.history = false;
      setTabLoading(prev => ({ ...prev, history: false }));
    }

    return () => {
      abortController.abort();
    };
  }, [project, allTasks, loadedTabs.history, showSnackbar, t]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Filter tasks locally based on needle
  const tasks = useMemo(() => {
    if (!filters.needle) {
      return allTasks;
    }
    const needleLower = filters.needle.toLowerCase();
    return allTasks.filter(task => {
      return (
        task.taskNumber?.toLowerCase().includes(needleLower) ||
        task.productName?.toLowerCase().includes(needleLower) ||
        task.description?.toLowerCase().includes(needleLower) ||
        task.assignedToUsername?.toLowerCase().includes(needleLower)
      );
    });
  }, [allTasks, filters.needle]);

  useEffect(() => {
    if (activeTab === 'tasks' && project) {
      fetchTasks();
    } else if (activeTab === 'quotes' && project?.receptionDocumentId) {
      fetchQuotes();
    } else if (activeTab === 'orders' && project?.receptionDocumentId) {
      fetchOrders();
    } else if (activeTab === 'deliveryNotes' && project?.id) {
      fetchDeliveryNotes();
    } else if (activeTab === 'documents' && project?.receptionDocumentId) {
      fetchDocuments();
    } else if (activeTab === 'history' && project) {
      fetchHistory();
    }
  }, [activeTab, project, fetchTasks, fetchQuotes, fetchOrders, fetchDeliveryNotes, fetchDocuments, fetchHistory]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters({
      needle: "",
    });
  };

  // ---- Add/Edit Task ----
  const handleAddTask = () => {
    setSelectedItem(null);
    setTaskFormOpen(true);
  };

  const handleTaskEdit = (item) => {
    setSelectedItem(item);
    setTaskFormOpen(true);
  };

  const handleTaskFormClose = () => {
    setTaskFormOpen(false);
    setSelectedItem(null);
    fetchTasks();
  };

  const handleTaskFormSuccess = (message) => {
    setTaskFormOpen(false);
    setSelectedItem(null);
    setLoadedTabs(prev => ({ ...prev, tasks: false }));
    // Force fetch from API after task modification
    fetchTasks(true);
    // Show snackbar message from TaskForm
    if (message) {
      showSnackbar(message, 'success');
    }
  };
  
  const handleModuleClose = useCallback((action) => {
    if (action === "submit") {
      if (activeTab === 'tasks') {
        setLoadedTabs(prev => ({ ...prev, tasks: false }));
        // Force fetch from API after task modification
        fetchTasks(true);
      } else if (activeTab === 'quotes') {
        setLoadedTabs(prev => ({ ...prev, quotes: false }));
        fetchQuotes();
      } else if (activeTab === 'orders') {
        setLoadedTabs(prev => ({ ...prev, orders: false }));
        fetchOrders();
      } else if (activeTab === 'deliveryNotes') {
        setLoadedTabs(prev => ({ ...prev, deliveryNotes: false }));
        fetchDeliveryNotes();
      } else if (activeTab === 'documents') {
        setLoadedTabs(prev => ({ ...prev, documents: false }));
        fetchDocuments();
      }
    }
    setShowModule(false);
    setSelectedItem(null);
  }, [activeTab, fetchTasks, fetchQuotes, fetchOrders, fetchDeliveryNotes, fetchDocuments]);

  // Document handlers
  const handleDocumentAddButtonClick = () => {
    setShowDocumentModule(true);
    setSelectedDocument(null);
  };

  const handleDocumentDelete = (document) => {
    setDocumentToDelete(document);
    setDeleteDocumentPopupOpen(true);
  };

  const handleDocumentDeleteConfirm = async () => {
    if (!documentToDelete || !project?.receptionDocumentId) return;
    try {
      await secureDelete(`/reception-documents/${project.receptionDocumentId}/attachments/${documentToDelete.id}`, showSnackbar);
      showSnackbar(t('confirmation.documentDelete'), 'success');
      setDeleteDocumentPopupOpen(false);
      setDocumentToDelete(null);
      // Refetch documents after deletion
      setLoadedTabs(prev => ({ ...prev, documents: false }));
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleDocumentDetails = (document) => {
    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    } else {
      console.error("No fileUrl available for document:", document);
    }
  };

  const handleDocumentDownload = (document) => {
    console.log("download", document);
  };

  const handleDocumentFilterChange = (filterName, filterValue) => {
    const updated = { ...documentFilters, [filterName]: filterValue };
    setDocumentFilters(updated);
    setLoadedTabs(prev => ({ ...prev, documents: false }));
  };

  const resetDocumentFilters = () => {
    const reset = { type: "" };
    setDocumentFilters(reset);
    setLoadedTabs(prev => ({ ...prev, documents: false }));
  };

  // ---- Task Action Handlers ----
  const handleTaskValidate = async (task) => {
    try {
      await securePut(`/projects/tasks/${task.id}/validate`, {
        validationStatus: "VALIDATED",
        rejectionReason: null,
      }, showSnackbar);
      showSnackbar(`Tâche ${task.taskNumber} validée`, "success");
      setLoadedTabs(prev => ({ ...prev, tasks: false }));
      // Force fetch from API after task validation
      fetchTasks(true);
    } catch (error) {
      console.error("Failed to validate task:", error);
    }
  };

  const handleTaskQualityIssue = async (task) => {
    try {
      await securePut(`/projects/tasks/${task.id}/validate`, {
        validationStatus: "REJECTED",
        rejectionReason: "Problème de qualité",
      }, showSnackbar);
      showSnackbar(`Problème de qualité signalé pour ${task.taskNumber}`, "warning");
      setLoadedTabs(prev => ({ ...prev, tasks: false }));
      // Force fetch from API after task rejection
      fetchTasks(true);
    } catch (error) {
      console.error("Failed to reject task:", error);
    }
  };

  // Format duration from minutes (integer) to "XhYm" format
  const formatDuration = (minutes) => {
    // Handle null, undefined, or 0
    if (!minutes || minutes === 0) return '0h';

    // Convert to number if it's a string
    const minutesNum = typeof minutes === 'string' ? parseInt(minutes, 10) : minutes;

    if (isNaN(minutesNum)) return '—';

    const wholeHours = Math.floor(minutesNum / 60);
    const mins = minutesNum % 60;

    if (mins === 0) {
      return `${wholeHours}h`;
    } else if (wholeHours === 0) {
      return `${mins}min`;
    } else {
      return `${wholeHours}h${mins}min`;
    }
  };

  // Calculate elapsed time (subtracting blocked and paused time)
  const calculateElapsedTime = (startDate, endDate, totalBlockedTime = 0, totalPausedTime = 0) => {
    if (!startDate) return null; // Empty if startDate is null

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(); // Use now if endDate is null

    // Calculate total elapsed time in minutes
    const diffMinutes = Math.floor((end - start) / (1000 * 60));

    // Subtract blocked and paused time (both are in minutes)
    const effectiveMinutes = Math.max(0, diffMinutes - (totalBlockedTime || 0) - (totalPausedTime || 0));

    const hours = Math.floor(effectiveMinutes / 60);
    const minutes = effectiveMinutes % 60;

    if (hours === 0 && minutes === 0) return '0min';
    if (hours === 0) return `${minutes}min`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h${minutes}min`;
  };

  // ---- Columns ----
  const taskColumns = [
    {
      label: "N° DE TÂCHE",
      key: "taskNumber",
      render: (taskNum, row) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-[16px] font-semibold text-[#38485C]">{taskNum}</span>
            {row.status === "BLOCKED" && row.blockedReason && (
              <div className="relative group">
                <div className="w-4 h-4 flex items-center justify-center cursor-pointer">
                  <img 
                    src={InfoIcon} 
                    alt="Info" 
                    className="w-full h-full"
                    style={{ filter: 'brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(7151%) hue-rotate(340deg) brightness(94%) contrast(92%)' }}
                  />
                </div>
                <div className="absolute left-1/2 bottom-full mb-2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  <div className="bg-[#16283E] text-white text-[12px] font-semibold px-3 py-2 rounded-[8px] shadow-lg relative max-w-xs break-words">
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0">
                      <div className="border-4 border-transparent border-t-[#16283E]"></div>
                    </div>
                    {t(`blocked_reasons.${row.blockedReason}`)}
                  </div>
                </div>
              </div>
            )}
          </div>
          {STATUS_CHIP(t(`task_status.${row.status}`))}
        </div>
      ),
    },
    {
      label: "SERVICE",
      key: "productName",
      render: (productName) => <span className="text-[16px] font-semibold text-[#38485C]">{productName || "—"}</span>,
    },
    {
      label: "DESCRIPTION",
      key: "description",
      render: (desc) => <span className="text-[16px] font-semibold text-[#38485C]">{desc || "—"}</span>,
    },
    {
      label: "DURÉE ESTIMÉE",
      key: "estimatedDuration",
      render: (duration) => <span className="text-[16px] font-semibold text-[#38485C]">{formatDuration(duration)}</span>,
    },
    {
      label: "TECHNICIEN",
      key: "assignedToUsername",
      render: (technician) => <span className="text-[16px] font-semibold text-[#38485C]">{technician || "—"}</span>,
    },
    {
      label: "HEURE DE DÉBUT",
      key: "startDate",
      splitDateTime: true,
      render: (time) => (
        <span className="text-[16px] font-semibold text-[#38485C]">
          {time ? formatDateTime(time) : "—"}
        </span>
      ),
    },
    {
      label: "HEURE DE FIN",
      key: "endDate",
      splitDateTime: true,
      render: (time) => (
        <span className="text-[16px] font-semibold text-[#38485C]">
          {time ? formatDateTime(time) : "—"}
        </span>
      ),
    },
    //ecoulé 
    {
      label: "TEMPS ÉCOULÉ",
      key: "elapsedTime",
      render: (elapsedTime, row) => (
        <span className="text-[16px] font-semibold text-[#38485C]">
          {calculateElapsedTime(row.startDate, row.endDate, row.totalBlockedTime, row.totalPausedTime) || '—'}
        </span>
      ),
    },
    ...(allTasks.some(task => task.status === "DONE") ? [
      {
        label: "STATUS DE VALIDATION",
        key: "validationStatus",
        render: (validationStatus, row) => (
          <span
          className="text-white text-[12px] font-semibold px-1 py-0.5 rounded-full inline-block w-fit text-center"
          style={{ backgroundColor: chipColor(validationStatus) }}
        >
          {t(`task_validation_status.${row.validationStatus}`)}
        </span>
        ),
      }
    ] : []),
    ...((role === "ROLE_WORKSHOP_MANAGER" || role === "ROLE_ADMIN") && allTasks.some(task => task.validationStatus !== "VALIDATED") ? [
    {
      label: "",
      key: "actions",
      align: "right",
      render: (_, task) => {
        // Don't show OptionsMenu if task is already validated
        if (task.validationStatus === "VALIDATED") {
          return null;
        }

        const options = [];

        // Show "Valider" and "Problème De Qualité" only if task status is DONE
        if (task.status === "DONE") {
          options.push({
            label: "Valider",
            icon: CheckIcon,
            color: "#06D6A0",
            action: () => handleTaskValidate(task),
          });
            if (task.validationStatus !== "REJECTED") {
          options.push({
            label: "Problème De Qualité",
            icon: XCircleIcon,
            color: "#EF466F",
            action: () => handleTaskQualityIssue(task),
          });
        }
          }
          if (task.validationStatus !== "VALIDATED" && task.status !== "DONE") {

        options.push({
          label: "Modifier",
          icon: EditIcon,
          action: () => handleTaskEdit(task),
        });
          }
        // Don't render OptionsMenu if no options
        if (options.length === 0) {
          return null;
        }

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <OptionsMenu
              options={options}
              direction="horizontal"
            />
          </div>
        );
      },
    },
    ] : []),
  ];

  const TaskFilters = [
    { 
      name: "needle",
      type: "search",
    },
  ];

  if (!project) {
    return null;
  }

  const handleGeneratePrefacture = async () => {
    setGeneratingPrefacture(true);
    try {
      await securePost(`/projects/${project.id}/generate-bill`, {}, showSnackbar);
      showSnackbar(t('confirmation.prefactureGenerated'), 'success');
      fetchProject();
    } catch (error) {
      console.error("Failed to generate prefacture:", error);
    } finally {
      setGeneratingPrefacture(false);
    }
  };

  const handleFinishProject = async () => {
    setFinishingProject(true);
    try {
    await securePut(`/projects/${project.id}/status?status=FINISHED`, {}, showSnackbar);
      fetchProject();
      showSnackbar(t('confirmation.projectFinished'), 'success');
    } catch (error) {
      console.error("Failed to finish project:", error);
    } finally {
      setFinishingProject(false);
    }
  };

  // ---- Header Props ----
  const headerProps = {
    entityId: project?.projectNumber ?? "—",
    status: t(`project_status.${project?.status}`),
    rawStatus: project?.status,
    client: { code: project?.clientCode, name: project?.clientName, to: `/clients/details/${vehicle?.clientId}` },
    complementary: { id: fr?.receptionNumber, to: `/fiche-reception/details/${fr?.id}` },
    createdAt: project?.createdAt ?? null,
    fuelPercent: fr?.fuelLevel ?? null,
    mileage: fr?.kilometers ?? null,
    vehicle: vehicle ? {
      brand: vehicle?.brand?.name || '—',
      model: vehicle?.model || '—',
      type: vehicle?.type || '—',
      color: vehicle?.color || '—',
      plate: vehicle?.immatriculationNumber || '—',
      vin: vehicle?.vinNumber || '—',
    } : null,
  };

  const headerActions = (
    <div className="flex items-center gap-3">
      {(role === "ROLE_SERVICE_CONSULTANT" || role === "ROLE_ADMIN") && (project?.status === "FINISHED") && (project?.bill === null) && (
        <button
          onClick={handleGeneratePrefacture}
          disabled={generatingPrefacture}
          className="flex items-center justify-center px-2 h-[40px] bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-xl text-primary font-bold space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generatingPrefacture ? (
            <>
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span>Chargement...</span>
            </>
          ) : (
            <>
              <img src={GenerateIcon} alt="" className="w-5 h-5" />
              <span>{t(`primaryButtons.generate_prefacture`)}</span>
            </>
          )}
        </button>
      )}
      {(role === "ROLE_WORKSHOP_MANAGER" || role === "ROLE_ADMIN") && project?.status !== "FINISHED" &&
        tasks.length > 0 &&
        tasks.every(task => task.status === "DONE" && task.validationStatus === "VALIDATED") && (
          <button
            onClick={handleFinishProject}
            disabled={finishingProject}
            className="flex items-center justify-center px-2 h-[40px] bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-xl text-primary font-bold space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {finishingProject ? (
              <>
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement...</span>
              </>
            ) : (
              <>
                <img src={checkIcon} alt="" className="w-5 h-5" />
                <span>{t(`primaryButtons.finish_project`)}</span>
              </>
            )}
          </button>
        )}
    </div>
  );

  const tasksCount = tasks?.length ?? 0;
  const quotesCount = quotes?.[0]?.revisionNumber ?? 0;
  const ordersCount = orders?.length ?? 0;
  const deliveryNotesCount = deliveryNotes?.length ?? 0;
  const documentsCount = documents?.length ?? 0;

  return (
    <div>
      <Helmet>
        <title>GrayFox — {t("titles.project_details")}</title>
      </Helmet>

      <div className="flex flex-col mb-[20px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col w-full">
            <Breadcrumbs />
            {loading || !project.id ? (
              <LoadingLogo />
            ) : (
              <DetailsHeader {...headerProps} rightActions={headerActions} />
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      {!loading && project.id && (
        <div className="flex bg-gray-200 rounded-[12px] p-1 w-full">
          <button
            className={`flex-1 px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === "tasks"
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab("tasks")}
          >
            {`${t("titles.tasks").toUpperCase()}`}
          </button>
          <button
            className={`flex-1 px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === "quotes"
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab("quotes")}
          >
            {`${t("titles.quotes").toUpperCase()}`}
          </button>
          <button
            className={`flex-1 px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === "orders"
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab("orders")}
          >
            {`${t("titles.order").toUpperCase()}`}
          </button>
          <button
            className={`flex-1 px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === "deliveryNotes"
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab("deliveryNotes")}
          >
            {`${t("titles.bl").toUpperCase()}`}
          </button>
          <button
            className={`flex-1 px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === "documents"
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab("documents")}
          >
            {`${t("titles.documents").toUpperCase()}`}
          </button>
          <button
            className={`flex-1 px-12 py-3 text-[14px] font-bold rounded-[12px] ${activeTab === "history"
              ? 'bg-white text-[#043F88] shadow'
              : 'text-[#ACAFC6]'
              }`}
            onClick={() => setActiveTab("history")}
          >
            {`${t("titles.history").toUpperCase()}`}
          </button>
        </div>
      )}

      {/* Tab content */}
      {!loading && project.id && activeTab === "tasks" && (
        <div className="pt-2">
          {tabLoading.tasks ? (
            <LoadingLogo />
          ) : (
            <>
              <div className="flex items-end gap-3 mb-4">
                <div className="flex-1">
                  <Filter
                    filters={TaskFilters}
                    onFilterChange={handleFilterChange}
                    filterValues={filters}
                    resetFilters={resetFilters}
                  />
                </div>
                {role === "ROLE_WORKSHOP_MANAGER" && (
                <button
                  type="button"
                  onClick={handleAddTask}
                  className="bg-[#16283E] w-[48px] h-[48px] rounded-[12px] flex items-center justify-center shadow-[0px_6px_26px_#626B7D12]"
                >
                  <img src={AddIcon} alt="Add" className="w-6 h-6" />
                </button>
                )}
              </div>
              <Rows columns={taskColumns} data={tasks} />
            </>
          )}
        </div>
      )}

      {!loading && project.id && activeTab === "quotes" && (
        <div className="pt-2">
          {tabLoading.quotes ? (
            <LoadingLogo />
          ) : quotes && quotes.length > 0 ? (
            quotes.map((quote) => {
              // Display only the latest version (the quote returned from API)
              return (
                <div key={quote.id} className="mb-8">
                  <div className="px-0 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-[24px] text-[#043F87] font-semibold">
                          ID {quote.quoteNumber || quote.id}
                        </h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-6 ml-auto">
                        <div className="flex items-center gap-2">
                          <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL HT:</span>
                          <span className="text-[20px] text-[#38485C] font-bold">
                            {formatAmount(quote.priceHT || 0)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL TTC:</span>
                          <span className="text-[20px] text-[#38485C] font-bold">
                            {formatAmount(quote.priceTTC || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {quote.details?.map((detail, index) => (
                      <ProductRow key={index} product={detail} variant="quote" />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-gray-500">
            </div>
          )}
        </div>
      )}

      {!loading && project.id && activeTab === "orders" && (
        <div className="pt-2">
          {tabLoading.orders ? (
            <LoadingLogo />
          ) : orders && orders.length > 0 ? (
            orders.map((order) => {
              return (
                <div key={order.id} className="mb-8">
                  <div className="px-0 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-[24px] text-[#043F87] font-semibold">
                          ID {order.cartNumber || order.id}
                        </h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-6 ml-auto">
                        <div className="flex items-center gap-2">
                          <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL HT:</span>
                          <span className="text-[20px] text-[#38485C] font-bold">
                            {formatAmount(order.priceHT || 0)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL TTC:</span>
                          <span className="text-[20px] text-[#38485C] font-bold">
                            {formatAmount(order.priceTTC || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {order.details?.map((detail, index) => (
                      <ProductRow key={index} product={detail} variant="quote" />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-gray-500">
            </div>
          )}
        </div>
      )}

      {!loading && project.id && activeTab === "deliveryNotes" && (
        <div className="pt-2">
          {tabLoading.deliveryNotes ? (
            <LoadingLogo />
          ) : deliveryNotes && deliveryNotes.length > 0 ? (
            deliveryNotes.map((bl) => {
              const details = bl.carts?.[0]?.details ?? bl.details ?? [];
              return (
                <div key={bl.id} className="mb-8">
                  <div className="px-0 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-[24px] text-[#043F87] font-semibold">
                          ID {bl.deliveryNoteNumber ?? bl.id}
                        </h3>
                      </div>
                      <div className="flex flex-wrap items-center gap-6 ml-auto">
                        <div className="flex items-center gap-2">
                          <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL HT:</span>
                          <span className="text-[20px] text-[#38485C] font-bold">
                            {formatAmount(bl.priceHT ?? 0)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[20px] text-[#3771B0] font-semibold">TOTAL TTC:</span>
                          <span className="text-[20px] text-[#38485C] font-bold">
                            {formatAmount(bl.priceTTC ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {details.map((detail, index) => (
                      <ProductRow key={index} product={detail} variant="quote" />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-gray-500">
            </div>
          )}
        </div>
      )}

      {!loading && project.id && activeTab === "documents" && (
        <div className="pt-2">
          {tabLoading.documents ? (
            <LoadingLogo />
          ) : (
            <>
              <div className="flex items-end gap-3 mb-3">
                <div className="flex-1">
                  <Filter
                    filters={[
                      {
                        name: "type",
                        type: "dropdown",
                        label: "Type de document",
                        options: [...documentTypeOptions],
                      }
                    ]}
                    onFilterChange={handleDocumentFilterChange}
                    resetFilters={resetDocumentFilters}
                    filterValues={documentFilters}
                  />
                </div>

                {/* Add button */}
                <button
                  type="button"
                  onClick={handleDocumentAddButtonClick}
                  className="bg-[#16283E] w-[48px] h-[48px] rounded-[12px] flex items-center justify-center"
                >
                  <img src={plusIcon} alt="Add" className="w-6 h-6" />
                </button>
              </div>

              {documents.length > 0 ? (
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  {documents.map((document, index) => (
                    <GenericCard
                      key={index}
                      title={t(`document_types.${document.type}`) || ''}
                      subtitle={document.originalFileName || ''}
                      optionsMenu={
                        <div onClick={(e) => e.stopPropagation()}>
                          <OptionsMenu options={[
                            { label: t('actions.details'), icon: EyeIcon, action: () => handleDocumentDetails(document) },
                            { label: t('actions.download'), icon: DownloadIcon, action: () => handleDocumentDownload(document) },
                            { label: t('actions.delete'), icon: DeleteIcon, color: '#EF466F', action: () => handleDocumentDelete(document) },
                          ]} />
                        </div>
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!loading && project.id && activeTab === "history" && (
        <div className="pt-2">
          {tabLoading.history ? (
            <LoadingLogo />
          ) : (
            <div className="space-y-3">
              {historyEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="bg-white rounded-[12px] p-4 shadow-[0px_6px_26px_#626B7D12] border border-gray-100"
                >
                  <div className="flex items-start gap-4">
                    {/* Colored dot */}
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0 mt-1"
                      style={{ backgroundColor: entry.dotColor }}
                    />

                    {/* Content */}
                    <div className="flex-1">
                      {/* User and timestamp */}
                      <div className="text-[14px] font-semibold text-[#3771B0] mb-1">
                        {formatDateTime(entry.date)}
                      </div>

                      {/* Description */}
                      <div className="text-[16px] font-medium text-[#38485C]">
                        {(() => {
                          // Remove any "at [date]" pattern from description (date is shown separately on first line)
                          let cleanDescription = entry.description;
                          // Remove pattern like "at DD-MM-YYYY HH:mm:ss" or "at [any date format]"
                          cleanDescription = cleanDescription.replace(/\s+at\s+\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}:\d{2}/gi, '');

                          return cleanDescription.split('**').map((part, index) => {
                            // Handle bold text (marked with **)
                            if (index % 2 === 1) {
                              const isBlocked = part.toLowerCase().includes('bloqué') || part.toLowerCase().includes('blocked');
                              const isCompleted = part.toLowerCase().includes('terminé') || part.toLowerCase().includes('terminées') || part.toLowerCase().includes('completed');

                              return (
                                <span
                                  key={index}
                                  className={`font-bold ${isBlocked ? 'text-[#EF466F]' :
                                      isCompleted ? 'text-[#06D6A0]' :
                                        'text-[#043F88]'
                                    }`}
                                >
                                  {part}
                                </span>
                              );
                            }
                            return <span key={index}>{part}</span>;
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {historyEntries.length === 0 && (
          <div className="text-center py-12 text-gray-500">
          </div>
              )}
        </div>
      )}
        </div>
      )}


      {showDocumentModule && (
        <ModuleWrapper
          moduleType="documents"
          mode="add"
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={{ receptionDocumentId: project?.receptionDocumentId }}
        />
      )}

      {deleteDocumentPopupOpen && (
        <DeletePopup
          open={deleteDocumentPopupOpen}
          onCancel={() => {
            setDeleteDocumentPopupOpen(false);
            setDocumentToDelete(null);
          }}
          onConfirm={handleDocumentDeleteConfirm}
        />
      )}

      {taskFormOpen && (
        <TaskForm
          title={selectedItem ? t('form_titles.edit_task') || 'Modifier la tâche' : t('form_titles.add_task') || 'Ajouter une tâche'}
          projectId={id}
          initialValues={selectedItem || {}}
          onSuccess={handleTaskFormSuccess}
          onCancel={handleTaskFormClose}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default ProjectsDetails;
